import { useState, useCallback, useEffect } from 'react';
import { ApiResponse, ListResponse } from '../types/api';

interface FetchParams {
  limit: number;
  offset: number;
  searchField?: string;
  status?: string;
  [key: string]: string | number | undefined;
}

interface UseInfiniteScrollOptions<T> {
  fetchFunction: (params: FetchParams) => Promise<ApiResponse<ListResponse<T>>>;
  limit?: number;
  searchTerm?: string;
  statusFilter?: string;
  additionalParams?: Record<string, string | number | undefined>;
}

interface UseInfiniteScrollReturn<T> {
  items: T[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => void;
  refresh: () => void;
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

export function useInfiniteScroll<T>({
  fetchFunction,
  limit = 20,
  searchTerm = '',
  statusFilter = '',
  additionalParams = {}
}: UseInfiniteScrollOptions<T>): UseInfiniteScrollReturn<T> {
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const loadItems = useCallback(async (reset = false) => {
    try {
      if (reset) {
        setIsLoading(true);
        setOffset(0);
        setError(null);
      } else {
        setIsLoadingMore(true);
      }

      const currentOffset = reset ? 0 : offset;
      const params = {
        limit,
        offset: currentOffset,
        searchField: searchTerm,
        status: statusFilter,
        ...additionalParams
      };

      const response = await fetchFunction(params);

      if (response.success && response.data) {
        // 다양한 키 이름 처리 (users, requests, contacts 등)
        const dataKey = Object.keys(response.data).find(
          key => Array.isArray(response.data![key as keyof typeof response.data])
        );
        const newItems = dataKey ? (response.data[dataKey as keyof typeof response.data] as T[]) : [];

        if (reset) {
          setItems(newItems);
        } else {
          setItems(prev => [...prev, ...newItems]);
        }

        setHasMore(response.data.pagination?.hasMore || false);
        setOffset(currentOffset + limit);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '데이터 로딩에 실패했습니다.');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [fetchFunction, limit, searchTerm, statusFilter, offset, additionalParams]);

  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      loadItems(false);
    }
  }, [isLoadingMore, hasMore, loadItems]);

  const refresh = useCallback(() => {
    loadItems(true);
  }, [loadItems]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const scrollPosition = target.scrollHeight - target.scrollTop;
    const clientHeight = target.clientHeight;

    if (scrollPosition <= clientHeight * 1.5 && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore]);

  // searchTerm 또는 statusFilter 변경 시 refresh
  useEffect(() => {
    loadItems(true);
  }, [searchTerm, statusFilter]);

  return {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    refresh,
    handleScroll
  };
}
